<template>
  <div class="widget-card widget-border">
    <dashboard-widget-header :title="config.name"></dashboard-widget-header>
    <div class="chart-form">
      <div class="date-range-chooser">
        <button
          v-for="option in dateRangeOptions"
          :key="option"
          class="date-range-option"
          :class="{ selected: option === dateRange}"
          :data-test="`date-range-${option.toLowerCase()}`"
          @click="selectRange(option)"
        >
          {{ option }}
        </button>
      </div>
      <div class="button-wrapper">
        <button data-test="decrement-date-offset" @click="offsetDecrement">
          <icon
            name="arrow-left-uui"
            variant="364152"
            class="arrow-icon"
          />
        </button>
        <span data-test="date-label" class="date-label">
          <icon
            name="calendar-uui"
            variant="364152"
            class="calendar-icon"
          />
          &nbsp;{{ dateLabel }}
        </span>
        <button data-test="increment-date-offset" :disabled="disableIncrement" :class="{ disabled: disableIncrement}" @click="offsetIncrement">
          <icon
            name="arrow-right-uui"
            variant="364152"
            class="arrow-icon"
          />
        </button>
      </div>
    </div>
    <div class="chart-container">
      <bar-chart ref="bar-chart" :chart-data="chartData" :chart-options="chartOptions"></bar-chart>
    </div>
  </div>
</template>

<script>
import DashboardWidgetHeader from '@components/dashboard-widgets/header';
import BarChart from '@/mixins/barChart';
import Icon from '@components/singletons/icon';

const DATE_RANGE_OPTIONS = ['Week', 'Month', 'Year'];

export default {
  components: { DashboardWidgetHeader, BarChart, Icon },
  props: {
    config: {
      type: Object,
    },
    dashboard: {
      type: Object,
    },
  },
  data() {
    return {
      dateOffset: 0,
      dateRange: 'Week',
      dateRangeOptions: DATE_RANGE_OPTIONS,
    };
  },
  computed: {
    chartData() {
      return {
        labels: this.labels,
        datasets: [
          {
            label: 'Questions',
            backgroundColor: '#326FF0',
            data: this.userData,
          },
        ],
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
                drawBorder: true,
              },
            },
          ],
          yAxes: [
            {
              gridLines: {
                display: true,
                drawBorder: false,
                color: '#EEF2F6',
                zeroLineColor: '#EEF2F6',
              },
              ticks: {
                beginAtZero: true,
                stepSize: this.stepSize,
                max: this.yAxisMax,
                padding: 10,
              },
            },
          ],
        },
      };
    },
    stepSize() {
      const max = Math.max(...this.userData);
      const validSteps = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];
      const index = validSteps.findIndex(step => step > max / 5);
      return validSteps[index];
    },
    yAxisMax() {
      const max = Math.max(...this.userData);
      const upperValue = max + (this.stepSize - (max % this.stepSize));
      return upperValue;
    },
    today() {
      return new Date();
    },
    rangeFirstDate() {
      if (this.dateRange === 'Week') {
        const offsetDate = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() + this.dateOffset * 7);
        return this.firstDateOfWeek(offsetDate);
      } else if (this.dateRange === 'Month') {
        return new Date(this.today.getFullYear(), this.today.getMonth() + this.dateOffset, 1);
      } else if (this.dateRange === 'Year') {
        return new Date(this.today.getFullYear() + this.dateOffset, 0, 1);
      }
    },
    rangeLastDate() {
      if (this.dateRange === 'Week') {
        return this.changeDateByDays(this.rangeFirstDate, 6);
      } else if (this.dateRange === 'Month') {
        const firstDayOfNextMonth = new Date(this.rangeFirstDate.getFullYear(), this.rangeFirstDate.getMonth() + 1, 1);
        return this.changeDateByDays(firstDayOfNextMonth, -1);
      } else if (this.dateRange === 'Year') {
        return new Date(this.rangeFirstDate.getFullYear(), 11, 31);
      }
    },
    labels() {
      if (this.dateRange === 'Week') {
        return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      } else if (this.dateRange === 'Month') {
        const labels = [];
        for (let date = new Date(this.rangeFirstDate); date <= this.rangeLastDate; date.setDate(date.getDate() + 1)) {
          labels.push(`${date.getDate()}`);
        }
        return labels;
      } else if (this.dateRange === 'Year') {
        return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Nov', 'Dec'];
      }
    },
    userFlashcardStats() {
      return this.$store.getters['user/stats'].flashcard || [];
    },
    userData() {
      if (!this.userFlashcardStats) {
        return [];
      }
      let data = [];

      if (this.dateRange === 'Month' || this.dateRange === 'Week') {
        for (let date = new Date(this.rangeFirstDate); date <= this.rangeLastDate; date.setDate(date.getDate() + 1)) {
          const count = this.userFlashcardStats.find((stat) => {
            return stat.date === this.dateFormatted(date);
          })?.count;
          data.push(count || 0);
        }
      } else if (this.dateRange === 'Year') {
        data = new Array(12).fill(0);
        this.userFlashcardStats.forEach(stat => {
          const currentYear = this.rangeFirstDate.getFullYear();
          const statYear = new Date(stat.date).getFullYear();
          if (currentYear !== statYear) {
            return;
          }
          const month = new Date(stat.date).getMonth();
          data[month] += stat.count;
        });
      }

      return data;
    },
    dateLabel () {
      if (this.dateRange === 'Week') {
        return `${this.dateFormattedForDateLabel(this.rangeFirstDate)} - ${this.dateFormattedForDateLabel(this.rangeLastDate)}`;
      } else if (this.dateRange === 'Month') {
        return `${this.rangeFirstDate.toLocaleString('default', { month: 'short' })} ${this.rangeFirstDate.getFullYear()}`;
      } else if (this.dateRange === 'Year') {
        return `${this.rangeFirstDate.getFullYear()}`;
      }
    },
    disableIncrement() {
      return this.dateOffset >= 0;
    },
  },
  methods: {
    dateFormatted(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based in JavaScript
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },
    dateFormattedForDateLabel(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1); // Months are 0-based in JavaScript
      const day = String(date.getDate());

      return `${month}/${day}/${year}`;
    },
    selectRange(range) {
      this.dateOffset = 0;
      this.dateRange = range;
    },
    changeDateByDays(date, days) {
      const result = new Date(date);
      result.setDate(result.getDate() + days);
      return result;
    },
    firstDateOfWeek(date) {
      const day = date.getDay() || 7;
      return new Date(date.getFullYear(), date.getMonth(), date.getDate() - day);
    },
    offsetIncrement() {
      if (this.dateOffset < 0) {
        this.dateOffset++;
      }
    },
    offsetDecrement() {
      this.dateOffset--;
    },
  },
};
</script>

<style scoped lang="scss">
.widget-card {
  padding: 2rem 1.5rem 1.5rem;
  height: rem-calc(518);
}

.widget-border {
  border: 1px solid #f1f1f1;
  border-radius: .5rem;
}

.chart-form {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  margin-top: 1.5rem;

  .button-wrapper {
    transform: translateY(-4rem);
    border-radius: .5rem;
    display: flex;
    gap: .375rem;

    .disabled {
      cursor: auto;
      opacity: 0.5;
    }

    >* {
      border: 1px solid #CDD5DF;
      border-radius: .5rem;
      height: rem-calc(40);
      color: #364152;
      font-weight: 600;
      font-size: rem-calc(14);
      line-height: rem-calc(40);
      padding: 0 rem-calc(12);
    }

    button {
      display: grid;
      place-items: center;
      width: rem-calc(40);
      background-color: white;
      text-align: center;
      padding: 0;
    }

    .arrow-icon {
      min-width: rem-calc(12);
    }

    .calendar-icon {
      transform: translateY(rem-calc(4));
      min-width: rem-calc(16);
    }
  }
}

@media screen and (max-width: 768px) {
  .chart-form .button-wrapper .date-label {
    display: none;
  }
}

.date-range-chooser {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 1rem;
  gap: 0.1rem;

  .date-range-option {
    height: rem-calc(36);
    width: rem-calc(68);
    border-radius: .5rem;
    color: #697586;
    background-color: white;
    font-weight: 600;
    font-size: rem-calc(14);
  }

  .selected {
    background-color: #eef2f6;
    color: #364152;
  }
}
</style>
